import React, { useState, useEffect, useMemo, useCallback, useId } from 'react';
import { supabase } from '../supabaseClient';
import buttonImage from '../buttonimage.webp';
import backgroundImage from '../background.webp';
import cardBackgroundImage from '../cardbackground.webp';
import { checkReadingLimit, handleDeleteReading } from '../utils/readingUtils';

// eslint-disable-next-line no-unused-vars
function Weaknesses({ goBack, goHome, initialCards, navigateTo }) {
    const [cards, setCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pickerVisible, setPickerVisible] = useState(false);
    const [selectedCards, setSelectedCards] = useState(initialCards || []);
    const [savedReadings, setSavedReadings] = useState([]);
    const [saving, setSaving] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [isLoadingPickedCards, setIsLoadingPickedCards] = useState(false);
    const [clickedCardId, setClickedCardId] = useState(null);
    const [worksheetText, setWorksheetText] = useState('');
    const [worksheetSaving, setWorksheetSaving] = useState(false);
    const [pendingText, setPendingText] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const fieldId = useId();
    const instructionsId = `${fieldId}-instructions`;
    const helpTextId = `${fieldId}-help`;
    const statusId = `${fieldId}-status`;
    const headingId = `${fieldId}-heading`;

    console.log('Weaknesses component mounted with props:', { goBack, goHome, initialCards });
    console.log('Initial selectedCards state:', selectedCards);

    const retrySupabaseQuery = useCallback(async (queryFn, retries = 5, delay = 1000) => {
        for (let attempt = 1; attempt <= retries; attempt++) {
            try {
                return await queryFn();
            } catch (error) {
                if (attempt < retries) {
                    console.warn(`Retrying query (attempt ${attempt})...`);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                } else {
                    throw error;
                }
            }
        }
    }, []);

    const fetchCards = useCallback(async () => {
        try {
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('cards')
                    .select('id, title, image_path, descriptions, card_number')
                    .eq('component', 'Astrology')
            );
            if (error) throw error;
            console.log('Fetched cards:', data);
            setCards(data || []);
        } catch (err) {
            alert('Failed to load cards.');
            console.error('Error fetching cards:', err.message);
            setCards([]);
        } finally {
            setLoading(false);
        }
    }, [retrySupabaseQuery]);

    const fetchSavedReadings = useCallback(async () => {
        try {
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) return;
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .select('*')
                    .eq('user_id', user.id)
                    .eq('component', 'Weaknesses')
                    .order('created_at', { ascending: false })
            );
            if (error) throw error;
            setSavedReadings(data || []);
        } catch (err) {
            console.error('Error fetching saved readings:', err.message);
        }
    }, [retrySupabaseQuery]);

    const fetchWorksheet = useCallback(async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                console.error('User not logged in');
                alert('Please log in to load worksheet.');
                return;
            }
            // Fetch from unencrypted weaknesses column
            const { data, error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('api')
                    .from('worksheets')
                    .select('weaknesses')
                    .eq('id', user.id)
                    .single()
            );
            if (error) {
                console.error('Error loading worksheet:', error.message);
                alert('Failed to load worksheet data.');
                setWorksheetText('');
                setPendingText('');
                return;
            }
            setWorksheetText(data?.weaknesses ?? '');
            setPendingText(data?.weaknesses ?? '');
        } catch (err) {
            console.error('Error fetching worksheet:', err.message);
            alert('Failed to load worksheet data.');
            setWorksheetText('');
            setPendingText('');
        }
    }, [retrySupabaseQuery]);

    const handleSaveWorksheet = async () => {
        if (worksheetText.length > 1000) {
            setStatusMessage('Worksheet text cannot exceed 1000 characters.');
            return;
        }
        if (!worksheetText.trim()) {
            setStatusMessage('Worksheet text cannot be empty.');
            return;
        }
        setWorksheetSaving(true);
        setStatusMessage('');
        setPendingText(worksheetText.trim());
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                setStatusMessage('User not logged in. Please log in to save worksheet.');
                return;
            }
            const payload = { id: user.id, weaknesses: worksheetText.trim() };
            const { error } = await retrySupabaseQuery(() =>
                supabase
                    .schema('api')
                    .from('worksheets')
                    .upsert(payload, { onConflict: 'id' })
            );
            if (error) {
                console.error('Supabase upsert error:', error.message, error.details || 'No details available');
                throw error;
            }
            setStatusMessage('Worksheet saved successfully.');
            await new Promise(resolve => setTimeout(resolve, 1000));
            await fetchWorksheet();
        } catch (err) {
            setStatusMessage('Failed to save worksheet. Retaining unsaved text.');
            console.error('Error saving worksheet:', err.message);
            setWorksheetText(pendingText);
        } finally {
            setWorksheetSaving(false);
        }
    };

    // Shuffle helper
    const shuffleArray = (array) => {
        const arr = array.slice();
        for (let i = arr.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [arr[i], arr[j]] = [arr[j], arr[i]];
        }
        return arr;
    };

    useEffect(() => {
        fetchCards();
        fetchSavedReadings();
        fetchWorksheet();
    }, [fetchCards, fetchSavedReadings, fetchWorksheet]);

    useEffect(() => {
        if (selectedCards.length > 0) {
            setIsLoadingPickedCards(true);
            const timer = setTimeout(() => setIsLoadingPickedCards(false), 500);
            return () => clearTimeout(timer);
        }
    }, [selectedCards]);

    const handleCardClick = (card) => {
        if (selectedCards.length < 8 && !selectedCards.some(c => c.id === card.id)) {
            setSelectedCards([...selectedCards, card]);
            setClickedCardId(card.id);
            setTimeout(() => setClickedCardId(null), 200);
        }
    };

    const handleSaveReading = async () => {
        if (selectedCards.length !== 8) {
            alert('Please select exactly 8 cards to save your reading.');
            return;
        }

        setSaving(true);
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                alert('User not logged in. Please log in to save your reading.');
                return;
            }

            const canSave = await checkReadingLimit(user.id, 'Weaknesses', retrySupabaseQuery, supabase);
            if (!canSave) {
                alert('You have reached the limit of 10 saved readings for this picker. Delete a reading to save a new one.');
                setSaving(false);
                return;
            }

            const descriptions = selectedCards.reduce((acc, card, index) => {
                const position = `position${index + 1}`;
                acc[position] = card.descriptions?.Weaknesses?.[position] || card.descriptions || 'No description available';
                return acc;
            }, {});

            const readingData = {
                user_id: user.id,
                component: 'Weaknesses',
                cards: selectedCards.map((card) => card.id),
                descriptions,
            };

            const { error: insertError } = await retrySupabaseQuery(() =>
                supabase
                    .schema('public')
                    .from('card_readings')
                    .insert(readingData)
            );

            if (insertError) {
                console.error('Supabase insert error:', insertError.message);
                throw insertError;
            }

            alert('Reading saved successfully!');
            setSelectedCards([]);
            setPickerVisible(false);
            await fetchSavedReadings();
        } catch (err) {
            alert('Failed to save reading. Please try again.');
            console.error('Error saving reading:', err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleLoadReading = (reading) => {
        // Prevent loading a saved reading before cards are fetched
        if (loading || cards.length === 0) {
            alert('Loading card data. Please wait a moment and try again.');
            return;
        }

        const cardsById = cards.reduce((acc, card) => {
            acc[card.id] = card;
            return acc;
        }, {});

        const loadedCards = (reading.cards || []).map((cardId) => cardsById[cardId]).filter(Boolean);

        if (loadedCards.length !== (reading.cards || []).length) {
            alert('Some cards could not be loaded for this saved reading. Please refresh and try again.');
        }

        setSelectedCards(loadedCards.map(card => ({
            ...card,
            descriptions: card.descriptions || 'No description available'
        })));
        setPickerVisible(false);
    };

    const handleSaveProgress = async () => {
        try {
            const { data: { user }, error: userError } = await supabase.auth.getUser();
            if (userError || !user) {
                alert('User not logged in. Please log in to save progress.');
                return;
            }

            const progressData = {
                user_id: user.id,
                current_component: 'Weaknesses',
                current_question_id: '8037d477-ed8d-4299-8104-d504b6f90b53',
                animated_helper: 'no', // always set to 'no'
                timestamp: new Date().toISOString(),
            };

            const { error: progressError } = await supabase
                .schema('api')
                .from('user_progress')
                .upsert(progressData);

            if (progressError) {
                console.error('Supabase upsert error:', progressError.message);
                alert('Failed to save progress: ' + progressError.message);
                return;
            }

            alert('Session progress has been saved successfully!');
        } catch (err) {
            alert('Failed to save progress. Please try again.');
            console.error('Error saving progress:', err.message);
        }
    };

    const handleStartReading = () => {
        setSelectedCards([]);
        setPickerVisible((prev) => {
            const next = !prev;
            // Shuffle cards every time picker is shown
            if (!prev && cards.length > 0) setCards(shuffleArray([...cards]));
            return next;
        });
    };

    // Memoize cards and savedReadings to prevent unnecessary re-renders
    const memoizedCards = useMemo(() => cards, [cards]);
    const memoizedSavedReadings = useMemo(() => savedReadings, [savedReadings]);

    // Helper to filter out back images from card set
    const isNotBackImage = (card) => {
        if (!card.image_path) return true;
        return !card.image_path.toLowerCase().includes('astrology back');
    };

    return (
        <div style={{
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '16px',
        }}>
            <style>
                {`
                @media (max-width: 600px) {
                    .weaknesses-btn-row {
                        flex-direction: row !important;
                        gap: 6px !important;
                    }
                    .weaknesses-btn {
                        width: 32vw !important;
                        min-width: 0 !important;
                        max-width: 100px !important;
                        padding: 10px 0 !important;
                        font-size: 16px !important;
                    }
                    .weaknesses-card-picker {
                        display: flex !important;
                        flex-direction: column !important;
                        flex-wrap: nowrap !important;
                        overflow-x: visible !important;
                        max-width: 100vw !important;
                        padding-bottom: 8px !important;
                        align-items: flex-start !important;
                        margin-left: 0 !important;
                    }
                    .weaknesses-card-row {
                        display: flex !important;
                        flex-direction: row !important;
                        flex-wrap: nowrap !important;
                        margin-top: 0 !important;
                        margin-bottom: 8px !important;
                        width: 100vw !important;
                        min-width: 100vw !important;
                        justify-content: flex-start !important;
                        align-items: flex-end !important;
                        overflow-x: hidden !important;
                    }
                    .weaknesses-card {
                        width: 48px !important;
                        min-width: 48px !important;
                        height: 64px !important;
                        margin-left: -36px !important;
                        margin-right: 0 !important;
                        transition: transform 0.2s, box-shadow 0.2s;
                        z-index: 1;
                    }
                    .weaknesses-card:first-child {
                        margin-left: 0 !important;
                    }
                }
                `}
            </style>
            <h3 style={{
                fontFamily: 'Times New Roman, Times, serif',
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'black',
                marginBottom: '32px',
            }}>
                Weaknesses
            </h3>

            {/* Session Helper Cards Row */}
            <div
                className="weaknesses-session-helper-row"
                style={{
                    display: 'flex',
                    flexDirection: window.innerWidth <= 600 ? 'column' : 'row',
                    gap: '16px',
                    width: '100%',
                    maxWidth: '600px',
                    marginBottom: '16px',
                }}
            >
                {/* Left Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    marginBottom: window.innerWidth <= 600 ? '16px' : 0,
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>• Weaknesses are areas where we struggle or underperform, often hard to spot due to self-bias or discomfort with vulnerability, yet they can foster growth when acknowledged. </div>
                        <div>•  Recognizing weaknesses requires honest self-assessment, often aided by reflecting on moments of frustration or failure without self-judgment. </div>
                        <div>• Weaknesses often hide in blind spots, revealed through feedback from others or recurring patterns of difficulty in specific tasks.</div>
                        <div>• Admitting weaknesses can feel threatening, but it builds resilience and opens opportunities to leverage complementary strengths in teams.</div>
                        <div>• Every weakness has a flip side that can be an asset when channeled intentionally in the right context.</div>
                        <div>• Embracing weaknesses as part of our humanity encourages growth and collaboration, transforming perceived flaws into areas of potential. </div>
                    </div>
                </div>
                {/* Right Card */}
                <div style={{
                    flex: 1,
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '16px',
                    textAlign: 'left',
                    backgroundImage: `url(${cardBackgroundImage})`,
                    backgroundSize: 'cover',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                }}>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '18px', marginBottom: '10px', textAlign: 'center' }}>*Session Helper*</div>
                    <div style={{ fontFamily: 'Times New Roman, Times, serif', fontWeight: 'bold', fontSize: '16px', margin: 0 }}>
                        <div>1. What task or situation do you consistently avoid, and what might that avoidance reveal about a challenge you face?</div>
                        <div>2. When have you felt frustrated by your own performance, and what specific skill or habit might have contributed to that struggle?</div>
                        <div>3. What feedback have others given you about areas where you could improve, and how might that area also contribute positively in certain situations?</div>
                        <div>4. What part of your work or personal life feels draining or difficult, and what underlying weakness might this point to?</div>
                        <div>5. If you could instantly improve one aspect of yourself, what would it be, and how could that trait already be helping you in unexpected ways?</div>
                        <div>6. What recurring obstacle do you face in achieving your goals, and how might that challenge reflect a strength when viewed from a different angle?</div>
                    </div>
                </div>
            </div>

            {/* WorksheetCard Section */}
            <div style={{
                width: '100%',
                maxWidth: '600px',
                border: '2px solid black',
                borderRadius: '10px',
                padding: '16px',
                backgroundImage: `url(${cardBackgroundImage})`,
                backgroundSize: 'cover',
                boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                marginBottom: '24px',
            }}>
                <div id={instructionsId} style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }} aria-hidden="false">
                    This is an editable worksheet. Type your answers in the text area below. When finished, use the Save Worksheet button. You will hear a confirmation when your worksheet is saved.
                </div>
                <div id={helpTextId} style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }} aria-hidden="false">
                    The worksheet can be edited freely. Use the save button after you finish making changes.
                </div>
                <div id={statusId} role="status" aria-live="polite" aria-atomic="true" style={{ position: 'absolute', left: '-9999px', height: '1px', width: '1px', overflow: 'hidden' }}>
                    {statusMessage}
                </div>
                <h4 id={headingId} style={{
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '20px',
                    fontWeight: 'bold',
                    color: 'black',
                    textAlign: 'center',
                    marginBottom: '16px',
                }}>
                    Weaknesses Worksheet
                </h4>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSaveWorksheet();
                    }}
                >
                    <label htmlFor={fieldId} style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        display: 'block',
                        marginBottom: '8px',
                    }}>
                        Enter your weaknesses worksheet notes.
                    </label>
                    <textarea
                        id={fieldId}
                        value={worksheetText}
                        onChange={(e) => {
                            setWorksheetText(e.target.value);
                            setStatusMessage('');
                        }}
                        maxLength={1000}
                        aria-labelledby={headingId}
                        aria-describedby={`${instructionsId} ${helpTextId}`}
                        aria-required="true"
                        style={{
                            width: '100%',
                            height: '300px',
                            border: '1px solid black',
                            borderRadius: '5px',
                            padding: '8px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            resize: 'none',
                            boxSizing: 'border-box',
                        }}
                        placeholder="Enter your text here (max 1000 characters)"
                    />
                    <p style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '14px',
                        color: 'black',
                        textAlign: 'right',
                        marginTop: '8px',
                    }}>
                        Characters: {worksheetText.length}/1000
                    </p>
                    <button
                        type="submit"
                        style={{
                            backgroundImage: `url(${buttonImage})`,
                            backgroundSize: 'cover',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '10px 20px',
                            width: '200px',
                            fontFamily: 'Times New Roman, Times, serif',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                            margin: '16px auto',
                            display: 'block',
                            color: 'black',
                            whiteSpace: 'nowrap',
                            opacity: worksheetSaving ? 0.7 : 1,
                        }}
                        disabled={worksheetSaving}
                    >
                        {worksheetSaving ? 'Saving...' : 'Save Worksheet'}
                    </button>
                </form>
            </div>

            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '16px',
                width: '100%',
                maxWidth: '600px',
            }}>
                {[
                    'There are a few reasons that knowing your weaknesses is beneficial to you. Often times when we are embarking on a wellness journey, we benefit from looking at ourselves and getting to know about our strengths, weaknesses, and values. Having these in mind helps us to navigate life in a way that allows more confidence and less fumbling. (not that fumbling is bad)',
                    'If we know what our weaknesses are, then we can take those into consideration when we make decisions. For example, if we know that we cannot ride a bike, then we will graciously decline an invitation to ride a bicycle down a steeply inclined city street; at least until such time as we have learned and practiced the skill of bike riding.',
                    'If we know that we have a tendency to fall into co-dependent relationships, then we can be cautious when someone comes offering to name us the hero while they stay in a victim role or vice versa.',
                    'These weaknesses do not define us. In fact, the list can change and should be updated periodically. These weaknesses might be hard for you to face. You might even realize that you have been doing a lot in order to try and hide these weaknesses from others. It can also be the case that you have been subjected to someone claiming that you have a weakness that you do not actually have. I encourage you to hold this list lightly and allow yourself to see how you feel about these items as you identify them.',
                    'The way you define what the weakness means to you or about you will heavily influence your experience of it. What do you know that you would not otherwise have known if you did not have the experience of having this weakness?',
                    'Make note of why you choose to define this as a weakness. Are you being fair and accurate? Have you adopted someone else\'s view of you as your own? Knowing yourself is a result of this self-inquiry around this topic. Both the items you list and the way you chose them and how you came to those conclusions all have information for you to glean. The things you identify as weaknesses can be things that you work to improve upon. Sometimes the improvement comes in the way you define the experience you are having with the weakness instead of changing your specific behavior or circumstance. ',
                    'EMBED VIDEO HERE',
                    'Below you will find an Oracle Card Picker that uses astrology symbolism and terms.  This card picker can be used to give you information about weaknesses to reflect on.  It matters how you think and feel about what your reading says.  Always trust yourself to interpret what the reading means for you.'
                ].map((text, index) => (
                    <div
                        key={index}
                        style={{
                            width: '100%',
                            border: '2px solid black',
                            borderRadius: '10px',
                            padding: '16px',
                            textAlign: 'center',
                            backgroundImage: `url(${cardBackgroundImage})`,
                            backgroundSize: 'cover',
                            boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        }}
                    >
                        {text === 'EMBED VIDEO HERE' ? (
                            <div style={{
                                width: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                <p
                                    style={{
                                        fontFamily: 'Times New Roman, Times, serif',
                                        fontSize: '16px',
                                        fontWeight: 'bold',
                                        color: 'black',
                                        textAlign: 'center',
                                        marginBottom: '12px',
                                    }}
                                >
                                    It is not necessary to listen to this video but it may be helpful if you have found yourself in victim consciousness and have been re-telling your stories of disempowerment repeatedly. It is not wrong to have done that.  This is just a perspective to consider that may help you validate why you have been doing that and how to shift things for yourself.
                                </p>
                                <div style={{
                                    position: 'relative',
                                    width: '100%',
                                    paddingBottom: '56.25%',
                                    height: 0,
                                    maxWidth: '480px',
                                    margin: '0 auto'
                                }}>
                                    <iframe
                                        src="https://www.youtube.com/embed/eWo_UvIRqh4?si=Nrw8fjxLqmHek4Bf&amp;controls=0"
                                        title="YouTube video player"
                                        frameBorder="0"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                        referrerPolicy="strict-origin-when-cross-origin"
                                        allowFullScreen
                                        style={{
                                            position: 'absolute',
                                            top: 0,
                                            left: 0,
                                            width: '100%',
                                            height: '100%',
                                            borderRadius: '10px',
                                            maxWidth: '100%',
                                        }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <p style={{
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                color: 'black',
                                textAlign: 'center',
                                lineHeight: '1.5',
                                marginBottom: '16px',
                            }}>
                                {text}
                            </p>
                        )}
                    </div>
                ))}
            </div>

            <button
                style={{
                    backgroundImage: `url(${buttonImage})`,
                    backgroundSize: 'cover',
                    border: '2px solid black',
                    borderRadius: '10px',
                    padding: '10px 20px',
                    width: '200px',
                    fontFamily: 'Times New Roman, Times, serif',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                    margin: '16px auto',
                    color: 'black',
                    whiteSpace: 'nowrap',
                }}
                onClick={handleStartReading}
            >
                {pickerVisible ? 'Hide Card Picker' : 'Start Card Reading'}
            </button>

            {pickerVisible && !loading && (
                <div
                    className="weaknesses-card-picker"
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexWrap: 'nowrap',
                        justifyContent: 'flex-start',
                        width: window.innerWidth > 600 ? `${Math.max(window.innerWidth - 400, 400)}px` : '100%',
                        maxWidth: window.innerWidth > 600 ? `${Math.max(window.innerWidth - 400, 400)}px` : '100%',
                        margin: window.innerWidth > 600 ? '0 auto' : '0 auto',
                        padding: '8px 6px',
                        overflow: 'hidden',
                        position: 'relative',
                        height: window.innerWidth > 600 ? undefined : undefined,
                        marginBottom: '0px',
                        paddingBottom: '0px',
                    }}
                >
                    {(() => {
                        const isMobile = window.innerWidth <= 600;
                        // Exclude back image from picker
                        const filteredCards = memoizedCards.filter(isNotBackImage);
                        const totalCards = filteredCards.length;
                        // For mobile, increase number of rows to 7 to reduce row width and eliminate scroll bar
                        const cardsPerRow = window.innerWidth > 600
                            ? 48
                            : Math.ceil(totalCards / 7); // 7 rows for mobile
                        // eslint-disable-next-line no-unused-vars
                        const numRows = Math.ceil(totalCards / cardsPerRow);
                        const cardWidth = isMobile ? 48 : 120;
                        const cardHeight = isMobile ? 64 : 100;
                        const containerWidth = isMobile
                            ? Math.min(window.innerWidth, 600)
                            : Math.max(window.innerWidth - 400, 400);
                        const effectiveContainerWidth = containerWidth - 12;

                        // For mobile, keep marginLeft -36px, but with more rows, each row is narrower
                        const overlapPx = isMobile ? 36 :
                            (cardsPerRow > 1
                                ? ((cardWidth * cardsPerRow - effectiveContainerWidth) / (cardsPerRow - 1))
                                : 0);

                        // Split cards into rows
                        const rows = [];
                        for (let i = 0; i < totalCards; i++) {
                            const rowIdx = Math.floor(i / cardsPerRow);
                            if (!rows[rowIdx]) rows[rowIdx] = [];
                            rows[rowIdx].push(filteredCards[i]);
                        }
                        return rows.map((row, rowIndex) => (
                            <div
                                key={rowIndex}
                                className={isMobile ? "weaknesses-card-row" : undefined}
                                style={{
                                    display: 'flex',
                                    flex: '0 0 100%',
                                    marginTop: 0,
                                    overflow: 'hidden',
                                    width: isMobile ? '100%' : `${effectiveContainerWidth}px`,
                                    minWidth: isMobile ? '100%' : `${effectiveContainerWidth}px`,
                                    justifyContent: 'flex-start',
                                    alignItems: 'flex-end',
                                    height: `${cardHeight}px`,
                                    position: 'relative',
                                    top: undefined,
                                    left: 0,
                                }}
                            >
                                {row.map((card, idx) => {
                                    let style = {
                                        width: `${cardWidth}px`,
                                        height: `${cardHeight}px`,
                                        minWidth: `${cardWidth}px`,
                                        marginLeft: idx === 0 ? 0 : `-${overlapPx}px`,
                                        marginRight: 0,
                                        cursor: 'pointer',
                                        boxShadow: clickedCardId === card.id
                                            ? '0 0 10px rgba(0, 255, 0, 0.5)'
                                            : '0 2px 4px rgba(0, 0, 0, 0.2)',
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                        transform: clickedCardId === card.id ? 'scale(1.1)' : 'scale(1)',
                                        zIndex: 1,
                                        backgroundImage: 'url(https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/card-images/Astrology/Astrology%20Back00.webp)',
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center',
                                        borderRadius: '8px',
                                    };
                                    return (
                                        <div
                                            key={card.id}
                                            className={isMobile ? "weaknesses-card" : undefined}
                                            style={style}
                                            onClick={() => handleCardClick(card)}
                                        />
                                    );
                                })}
                            </div>
                        ));
                    })()}
                </div>
            )}
            {/* Remove any spacer div between picker and reading display */}
            {selectedCards.filter(isNotBackImage).length > 0 && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(4, 1fr)',
                    gridTemplateRows: 'repeat(2, 1fr)',
                    gap: '4px',
                    marginTop: '0px', // Ensure no extra space
                    background: 'white',
                    border: '2px solid black',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    padding: '8px',
                    borderRadius: '8px',
                    maxWidth: '600px',
                    margin: '16px auto',
                    width: '100%',
                }}>
                    {selectedCards
                        .filter(isNotBackImage)
                        .map((card, index) => {
                        const labels = [
                            'Known Room for Improvement',
                            'Already Improving',
                            'Subconscious Sabotage',
                            'Practice Pays Off',
                            'Compensating',
                            'Repetitive Issue',
                            'Offsetting Issue',
                            'Build Skills'
                        ];
                        return (
                            <div key={card.id} style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.3), 0 -4px 6px rgba(0,0,0,0.3)',
                            }}>
                                <img
                                    src={`https://zxvjqkrfvproyblrjcst.supabase.co/storage/v1/object/public/${card.image_path}`}
                                    alt={card.title}
                                    style={{ width: '100px', height: '100px', objectFit: 'contain' }}
                                    onError={() => console.error(`Failed to load card image: ${card.image_path}`)}
                                />
                                <p style={{
                                    fontFamily: 'Times New Roman, Times, serif',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                    marginTop: '8px',
                                    textAlign: 'center',
                                    wordWrap: 'break-word',
                                    maxWidth: '100px',
                                }}>
                                    {labels[index] || 'No description available'}
                                </p>
                            </div>
                        );
                    })}
                </div>
            )}

            {selectedCards.length === 8 && (
                <button
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '200px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        margin: '16px auto',
                        color: 'black',
                        whiteSpace: 'nowrap',
                    }}
                    onClick={handleSaveReading}
                    disabled={saving}
                >
                    {saving ? 'Saving...' : 'Save Reading'}
                </button>
            )}

            <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '16px',
                marginTop: '32px',
            }}>
                <button
                    className="weaknesses-btn"
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        color: 'black',
                    }}
                    onClick={goBack}
                >Back</button>
                <button
                    className="weaknesses-btn"
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        color: 'black',
                    }}
                    onClick={goHome}
                >Home</button>
                <button
                    className="weaknesses-btn"
                    style={{
                        backgroundImage: `url(${buttonImage})`,
                        backgroundSize: 'cover',
                        border: '2px solid black',
                        borderRadius: '10px',
                        padding: '10px 20px',
                        width: '120px',
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        boxShadow: '3px 3px 5px rgba(0,0,0,0.3)',
                        color: 'black',
                    }}
                    onClick={handleSaveProgress}
                >Save</button>
            </div>

            {memoizedSavedReadings.length > 0 && (
                <div style={{
                    marginTop: '32px',
                    width: '100%',
                    maxWidth: '600px',
                    padding: '8px',
                }}>
                    <h4 style={{
                        fontFamily: 'Times New Roman, Times, serif',
                        fontSize: '20px',
                        fontWeight: 'bold',
                        color: 'black',
                        textAlign: 'center',
                        marginBottom: '16px',
                    }}>
                        Saved Readings
                    </h4>
                    {memoizedSavedReadings.map((reading) => (
                        <div
                            key={reading.id}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: '8px',
                                border: '1px solid black',
                                borderRadius: '8px',
                                marginBottom: '8px',
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontFamily: 'Times New Roman, Times, serif',
                                fontSize: '14px',
                            }}
                        >
                            <span onClick={() => handleLoadReading(reading)}>
                                {new Date(reading.created_at).toLocaleString()}
                            </span>
                            <span
                                style={{
                                    color: 'red',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteReading(reading.id, fetchSavedReadings, retrySupabaseQuery, supabase);
                                }}
                            >
                                -delete
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

export default Weaknesses;